/**
 * Шум Sentry в админке, который не является багом в нашем коде.
 * Паттерны выверены по реальным issue'ам (2023–2026).
 */
export const SENTRY_IGNORED_ERROR_PATTERNS: Array<RegExp> = [
  // WEB-1 (7651 событий, 152 пользователя, с 2023 года).
  // Axios кидает Cancel/Abort как отклонённый промис с undefined —
  // Sentry ловит их как "Non-Error promise rejection captured with value: undefined".
  // Это не баг, а нормальный сценарий отмены запроса при навигации.
  /Non-Error promise rejection captured with value: undefined/,

  // WEB-2Z / WEB-2Y: баг LightGallery — при быстром переключении slide=undefined.
  // Исправить можно только апгрейдом библиотеки, не наш код.
  /Cannot read properties of undefined \(reading '__slideVideoInfo'\)/,

  // Сетевые отмены на клиенте: пользователь закрыл вкладку / потерял сеть.
  // Для реальных серверных ошибок у нас есть 5xx-логи.
  /^Failed to fetch$/,
  /^Load failed$/,
  /NetworkError when attempting to fetch resource/,
  /The user aborted a request/,
  /^AbortError/,
  // WEB-4Q / WEB-5S: axios-таймаут с локализованным сообщением.
  // Суть — сетевая проблема на стороне клиента, не баг приложения.
  /Превышен таймаут/,
  /timeout of \d+ms exceeded/,

  // WEB-1R (969 событий, 10 пользователей, с 2023).
  // Axios при 5xx от upstream (чаще всего nginx 502 Bad Gateway от
  // проксирующего слоя перед backend) отдаёт в промис отклонение с
  // HTML-телом ошибки. Sentry эмитит это как:
  //   "UnhandledRejection: Non-Error promise rejection captured with value: <html>...502 Bad Gateway..."
  // На сервере мы уже видим исходную 5xx-причину — дубль HTML с админки не нужен.
  //
  // Матчим характерную пару: стандартное "Non-Error promise rejection" от
  // Sentry + HTML (опционально с `<!DOCTYPE html>`) + nginx/Cloudflare
  // статус-фраза с настоящим 5xx-кодом. Произвольные сообщения с подстрокой
  // "<html>" или 4xx-страницы сюда не попадают.
  /Non-Error promise rejection captured with value:\s*(?:<!DOCTYPE html>\s*)?<html>[\s\S]*?\b5\d{2}\s+(?:Bad Gateway|Service Unavailable|Gateway Timeout|Internal Server Error)\b/i,
  // WEB-29 (660 событий, 10 пользователей, с 2024).
  // Форма события (Sentry title): "UnhandledRejection: Object captured as
  // promise rejection with keys: error", дополнительные данные содержат
  // `{ error: "Transaction API error: Transaction already closed: ..." }`.
  // Это серверная Prisma-ошибка, которую axios пробрасывает в promise
  // rejection, а Sentry сериализует объект. Сам баг уже виден у backend.
  //
  // Поддерживаем и чистую строку (как описано в Sentry SDK), и вариант с
  // префиксом "UnhandledRejection: " который показывает UI Sentry.
  /^(?:UnhandledRejection:\s+)?Object captured as promise rejection with keys: error$/,

  // WEB-32 (990 событий / 2 пользователя): NotFoundError из react-dom@18 в проде.
  // Классический симптом вмешательства Google Translate / похожего расширения
  // в commit-фазу React: расширение перетасовывает DOM-узлы, и React
  // не может найти ребёнка для удаления. Не наш код.
  // Сознательно НЕ фильтруем общие "Cannot read properties of null (reading 'parentNode')"
  // — слишком широко, может скрыть реальные null-deref баги. Если parentNode-вариант
  // того же семейства появится в Sentry — добавим отдельно с проверкой stack-frame.
  /Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node/,
];

/**
 * Подстроки в путях Sentry-фрейма, которые означают, что ошибка прилетела
 * из стороннего пакета и не является нашим багом. Используется как
 * дополнительный гейт по стектрейсу (когда message слишком общее, чтобы
 * безопасно матчить по нему). Проверяем все три места, куда Sentry может
 * положить путь к источнику: filename, abs_path, module.
 */
export const SENTRY_IGNORED_FRAME_SUBSTRINGS: ReadonlyArray<string> = [
  // WEB-37 (415 событий, 3 пользователя, с 2024-06).
  // `TypeError: Cannot read properties of undefined (reading 'height')` в
  // `Zoom.prototype.getDragAllowedAxises` из lightgallery@2.7.2 — известный
  // баг zoom-плагина: при mousedown на ещё не инициализированный слайд
  // `this.containerRect` остаётся undefined. Поправить можно только апгрейдом
  // либы (как и WEB-2Y/2Z). Сообщение слишком общее, поэтому фильтруем по
  // фрейму, а не по тексту, чтобы не глушить настоящие ошибки `.height` в
  // нашем коде.
  'lightgallery/plugins/zoom/lg-zoom',
];

export function isIgnoredSentryMessage(message: unknown): boolean {
  if (typeof message !== 'string' || !message) return false;
  return SENTRY_IGNORED_ERROR_PATTERNS.some((p) => p.test(message));
}

type SentryFrame = {
  filename?: string;
  abs_path?: string;
  module?: string;
};
type SentryEvent = {
  message?: string;
  exception?: {
    values?: Array<{
      value?: string;
      stacktrace?: { frames?: SentryFrame[] };
    }>;
  };
};

function frameMatches(frame: SentryFrame): boolean {
  // Бандлеры/source-maps по-разному кладут путь к источнику. Безопаснее
  // проверять все три поля и не зависеть от того, что включено для конкретного
  // ивента. (Например, минифицированный браузерный фрейм может иметь только
  // abs_path = "https://admin/static/js/main.abc.js" без filename, а
  // source-mapped — наоборот.)
  for (const candidate of [frame.filename, frame.abs_path, frame.module]) {
    if (typeof candidate !== 'string') continue;
    for (const sub of SENTRY_IGNORED_FRAME_SUBSTRINGS) {
      if (candidate.includes(sub)) return true;
    }
  }
  return false;
}

function hasIgnoredFrame(event: SentryEvent): boolean {
  const frames = event.exception?.values?.flatMap(
    (v) => v.stacktrace?.frames ?? [],
  );
  if (!frames || frames.length === 0) return false;
  return frames.some(frameMatches);
}

export function sentryBeforeSend<E extends SentryEvent>(
  event: E,
  hint?: { originalException?: unknown },
): E | null {
  const original = hint?.originalException;
  const originalMessage =
    original instanceof Error ? original.message : undefined;
  const eventMessage =
    event.message ?? event.exception?.values?.[0]?.value ?? undefined;

  if (
    isIgnoredSentryMessage(originalMessage) ||
    isIgnoredSentryMessage(eventMessage) ||
    hasIgnoredFrame(event)
  ) {
    return null;
  }
  return event;
}
